import { useEffect, useMemo } from 'react';

import language from '@/config/language';
import { setLanguage } from '@/state/reducers/languageSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { Tab } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../state/hooks';
import { selectSettings, setSettings } from '../../../state/reducers/settingSlice';
import BuildVersion from '../../common/BuildVersion';
import Divider from '../../common/Divider';
import { SettingsFormInput, settingsSchema } from '../../form/schema/settingsSchema';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTab, ModalTabList, ModalTitle } from '../Modal';
import AdvancedTab, { idleTimeoutOptions } from './AdvancedTab';
import GeneralTab, { millisecondShowOptions, timezoneOptions } from './GeneralTab';
import NotificationsTab, { notificationDurationOptions, notificationShowOptions } from './NotificationsTab';
import TradingTab, {
    defaultLimitOrderDurationOptions,
    defaultMarketOrderDurationOptions,
    defaultStopOrderDurationOptions,
    shortSellPreventionOptions,
    tradingLayoutOptions,
    twoClickTradingOptions
} from './TradingTab';

const defaultTradingLayout = window.config?.layout?.default;
const idleTimer = +(window.config.idleTimer || 1200000);

const defaultValues: Partial<SettingsFormInput> = {
    general: {
        timezone: timezoneOptions[0],
        idleTimeout: idleTimeoutOptions[0],
        theme: { label: 'Dark Theme', value: 'dark' },
        language: { label: `${language['en'].common['en']} (UK)`, value: 'en' },
        showMilliseconds: millisecondShowOptions[0]
    },
    notifications: {
        show: notificationShowOptions[1]
        // duration: notificationDurationOptions[1]
    },
    trading: {
        tradingLayout: defaultTradingLayout === 'xplorspot' ? tradingLayoutOptions[0] : tradingLayoutOptions[1],
        twoClickTrading: twoClickTradingOptions[0],
        shortSellPrevention: shortSellPreventionOptions[0],
        defaultMarketOrderDuration: defaultMarketOrderDurationOptions[0] as any,
        defaultLimitOrderDuration: defaultLimitOrderDurationOptions[0] as any,
        defaultStopOrderDuration: defaultStopOrderDurationOptions[0] as any
    }
};

const SettingsModal = (props: ModalProps) => {
    const { opened, handlers } = props;

    const dispatch = useDispatch();
    const settings = useAppSelector(selectSettings);

    const lang = useLanguage();
    const tabs = useMemo(
        () => [
            lang.settings.generalTab,
            lang.settings.tradingTab,
            lang.settings.notificationsTab,
            lang.settings.userTab
        ],
        [lang]
    );

    const form = useForm<SettingsFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(settingsSchema)
    });
    const { handleSubmit, reset } = form;

    const onSubmit = async (data: SettingsFormInput, e) => {
        dispatch(
            setSettings({
                ...data,
                notifications: {
                    ...data.notifications,
                    duration: notificationDurationOptions[1]
                }
            } as any)
        );
        if (data.general?.language.value) dispatch(setLanguage(data.general.language.value));
        handlers.close();
    };

    useEffect(() => {
        if (opened) {
            reset({
                general: {
                    ...defaultValues.general,
                    ...settings.general,
                    idleTimeout: settings.general?.idleTimeout || {
                        label: '20 Minutes',
                        value: idleTimer
                    },
                    theme: settings.general?.theme
                        ? {
                              label: settings.general.theme.value === 'dark' ? lang.common.dark : lang.common.light,
                              value: settings.general.theme.value
                          }
                        : { label: lang.common.dark, value: 'dark' },
                    language: settings.general?.language || {
                        label: `${language['en'].common['en']} (UK)`,
                        value: 'en'
                    },
                    showMilliseconds: settings.general?.showMilliseconds || millisecondShowOptions[0]
                },
                notifications: {
                    ...defaultValues.notifications,
                    ...settings.notifications
                },
                trading: {
                    ...defaultValues.trading,
                    ...(settings.trading as any)
                }
            });
        }
    }, [opened]);

    return (
        <Modal {...props} size="max-w-xl">
            <FormProvider {...form}>
                <form className="h-full flex flex-col lg:block lg:h-auto" onSubmit={handleSubmit(onSubmit)}>
                    <ModalHeader>
                        <ModalTitle>{lang.navigation.settings}</ModalTitle>
                        <ModalClose handlers={handlers} />
                    </ModalHeader>
                    <Divider />
                    <div className="h-full flex flex-col lg:h-auto lg:block w-full text-brand-text mt-[1px]">
                        <Tab.Group>
                            <ModalTabList>
                                {tabs.map((tab) => (
                                    <ModalTab key={tab}>{tab}</ModalTab>
                                ))}
                            </ModalTabList>
                            <Tab.Panels className="flex-1 basis-0 overflow-y-auto">
                                <GeneralTab />
                                <TradingTab />
                                <NotificationsTab />
                                <AdvancedTab />
                            </Tab.Panels>
                        </Tab.Group>
                    </div>
                    <Divider />
                    <div className="flex flex-row justify-between text-brand-text text-sm items-center p-2 sm:p-4">
                        <BuildVersion />
                        <div className="justify-end flex space-x-2">
                            <button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                                onClick={handlers.close}>
                                {lang.common.cancel}
                            </button>
                            <button
                                type="submit"
                                className="rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light">
                                {lang.common.saveChanges}
                            </button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default SettingsModal;
